<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import inboxesAPI from 'dashboard/api/inboxes';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const POLL_INTERVAL = 4000;

const { t } = useI18n();

const report = ref(null);
const conversion = ref(null);
const isLoading = ref(false);
const isSubmitting = ref(false);
const apiKey = ref('');
const consolidateConversations = ref(true);
const convertDialogRef = ref(null);
const rollbackDialogRef = ref(null);
let pollTimer = null;

const isApiInbox = computed(() => props.inbox.channel_type === 'Channel::Api');
const evolution = computed(() => report.value?.evolution ?? {});
const blockers = computed(() => report.value?.blockers ?? []);
const warnings = computed(() => report.value?.warnings ?? []);
const conversionResult = computed(() => conversion.value?.result ?? {});
const isRunning = computed(() =>
  ['pending', 'running'].includes(conversion.value?.status)
);
const canConvert = computed(
  () => isApiInbox.value && !blockers.value.length && !isRunning.value
);
const canRollback = computed(() =>
  ['completed', 'failed'].includes(conversion.value?.status)
);

const fetchReport = async () => {
  isLoading.value = true;
  try {
    const { data } = await inboxesAPI.evolutionConversionPreview(
      props.inbox.id,
      { apiKey: apiKey.value }
    );
    report.value = data;
  } catch (error) {
    useAlert(t('INBOX_MGMT.EVOLUTION_CONVERSION.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// The conversion runs in a background job, so the screen follows it by polling until it settles.
const fetchConversion = async () => {
  try {
    const { data } = await inboxesAPI.evolutionConversion(props.inbox.id);
    conversion.value = data;
  } catch (error) {
    conversion.value = null;
  }
  if (isRunning.value) {
    pollTimer = setTimeout(fetchConversion, POLL_INTERVAL);
  }
};

const runConversion = async () => {
  isSubmitting.value = true;
  try {
    const { data } = await inboxesAPI.convertToEvolution(props.inbox.id, {
      apiKey: apiKey.value,
      consolidateConversations: consolidateConversations.value,
    });
    conversion.value = data;
    convertDialogRef.value?.close();
    pollTimer = setTimeout(fetchConversion, POLL_INTERVAL);
  } catch (error) {
    useAlert(t('INBOX_MGMT.EVOLUTION_CONVERSION.RUN.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const runRollback = async () => {
  isSubmitting.value = true;
  try {
    const { data } = await inboxesAPI.rollbackEvolutionConversion(
      props.inbox.id
    );
    conversion.value = data;
    rollbackDialogRef.value?.close();
    pollTimer = setTimeout(fetchConversion, POLL_INTERVAL);
  } catch (error) {
    useAlert(t('INBOX_MGMT.EVOLUTION_CONVERSION.ROLLBACK.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

// The database side of the report costs nothing to load, so it is there before the admin asks;
// the API key below only adds the live check against the Evolution server.
onMounted(() => {
  fetchConversion();
  if (isApiInbox.value) fetchReport();
});

onBeforeUnmount(() => clearTimeout(pollTimer));

const contactStats = computed(() => {
  const stats = report.value?.contact_inboxes ?? {};
  return [
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.CONTACTS.TOTAL'),
      value: stats.total,
    },
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.CONTACTS.DISCARDABLE'),
      value: stats.discardable,
    },
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.CONTACTS.WITHOUT_PHONE'),
      value: stats.without_phone,
      warn: stats.without_phone > 0,
    },
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.CONTACTS.GROUPS'),
      value: stats.groups,
    },
  ];
});

const conflictStats = computed(() => {
  const stats = report.value?.contact_inboxes ?? {};
  return [
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.CONFLICTS.SHARED_NUMBERS'),
      value: stats.shared_numbers,
    },
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.CONFLICTS.MERGE_CONFLICTS'),
      value: stats.merge_conflicts,
      warn: stats.merge_conflicts > 0,
    },
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.CONFLICTS.MANY_CONTACTS'),
      value: stats.numbers_with_many_contacts,
      warn: stats.numbers_with_many_contacts > 0,
    },
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.CONFLICTS.LID_DISGUISED'),
      value: stats.lid_disguised,
      warn: stats.lid_disguised > 0,
    },
  ];
});

const conversationStats = computed(() => {
  const stats = report.value?.conversations ?? {};
  const consolidation = stats.consolidation ?? {};
  return [
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.CONVERSATIONS.TOTAL'),
      value: stats.total,
    },
    {
      label: t(
        'INBOX_MGMT.EVOLUTION_CONVERSION.CONVERSATIONS.CONTACTS_WITH_MANY'
      ),
      value: consolidation.contacts_with_many,
    },
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.CONVERSATIONS.MERGED_AWAY'),
      value: consolidation.conversations_merged_away,
    },
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.CONVERSATIONS.MAX_PER_CONTACT'),
      value: consolidation.max_per_contact,
    },
  ];
});

const messageStats = computed(() => {
  const stats = report.value?.messages ?? {};
  return [
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.MESSAGES.TOTAL'),
      value: stats.total,
    },
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.MESSAGES.WITH_WAID'),
      value: stats.with_waid,
    },
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.MESSAGES.WITHOUT_SOURCE_ID'),
      value: stats.without_source_id,
    },
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.MESSAGES.ORPHANED'),
      value: stats.orphaned,
      warn: stats.orphaned > 0,
    },
  ];
});

const sections = computed(() => [
  {
    title: t('INBOX_MGMT.EVOLUTION_CONVERSION.CONTACTS.TITLE'),
    stats: contactStats.value,
  },
  {
    title: t('INBOX_MGMT.EVOLUTION_CONVERSION.CONFLICTS.TITLE'),
    stats: conflictStats.value,
  },
  {
    title: t('INBOX_MGMT.EVOLUTION_CONVERSION.CONVERSATIONS.TITLE'),
    stats: conversationStats.value,
  },
  {
    title: t('INBOX_MGMT.EVOLUTION_CONVERSION.MESSAGES.TITLE'),
    stats: messageStats.value,
  },
]);

const resultStats = computed(() => {
  const result = conversionResult.value;
  const consolidation = result.consolidation ?? {};
  return [
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.RESULT.CONTACT_INBOXES'),
      value: result.contact_inboxes_rewritten,
    },
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.RESULT.CONVERSATIONS_MOVED'),
      value: result.conversations_moved,
    },
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.RESULT.CONVERSATIONS_MERGED'),
      value: consolidation.conversations_merged,
    },
    {
      label: t('INBOX_MGMT.EVOLUTION_CONVERSION.RESULT.SKIPPED'),
      value: result.contact_inboxes_skipped,
      warn: result.contact_inboxes_skipped > 0,
    },
  ];
});

const conflictSamples = computed(
  () => report.value?.contact_inboxes?.conflict_samples ?? []
);

const statusLabel = computed(() => {
  switch (conversion.value?.status) {
    case 'pending':
      return t('INBOX_MGMT.EVOLUTION_CONVERSION.STATUS.PENDING');
    case 'running':
      return t('INBOX_MGMT.EVOLUTION_CONVERSION.STATUS.RUNNING');
    case 'completed':
      return t('INBOX_MGMT.EVOLUTION_CONVERSION.STATUS.COMPLETED');
    case 'failed':
      return t('INBOX_MGMT.EVOLUTION_CONVERSION.STATUS.FAILED');
    case 'rolled_back':
      return t('INBOX_MGMT.EVOLUTION_CONVERSION.STATUS.ROLLED_BACK');
    default:
      return '';
  }
});

// Every code the analyzer can emit gets its own literal translation key so the details it carries
// (an inbox name, a count) land in the sentence instead of being dumped as raw JSON.
const issueText = issue => {
  const details = issue.details ?? {};
  switch (issue.code) {
    case 'not_an_api_inbox':
      return t('INBOX_MGMT.EVOLUTION_CONVERSION.ISSUES.NOT_AN_API_INBOX');
    case 'instance_name_unknown':
      return t('INBOX_MGMT.EVOLUTION_CONVERSION.ISSUES.INSTANCE_NAME_UNKNOWN');
    case 'instance_used_by_another_inbox':
      return details.inbox_name
        ? t(
            'INBOX_MGMT.EVOLUTION_CONVERSION.ISSUES.INSTANCE_USED_BY_INBOX',
            details
          )
        : t('INBOX_MGMT.EVOLUTION_CONVERSION.ISSUES.INSTANCE_USED_ELSEWHERE');
    case 'instance_already_native':
      return details.inbox_name
        ? t(
            'INBOX_MGMT.EVOLUTION_CONVERSION.ISSUES.INSTANCE_ALREADY_NATIVE',
            details
          )
        : t('INBOX_MGMT.EVOLUTION_CONVERSION.ISSUES.INSTANCE_USED_ELSEWHERE');
    case 'phone_number_taken':
      return t(
        'INBOX_MGMT.EVOLUTION_CONVERSION.ISSUES.PHONE_NUMBER_TAKEN',
        details
      );
    case 'instance_not_connected':
      return t(
        'INBOX_MGMT.EVOLUTION_CONVERSION.ISSUES.INSTANCE_NOT_CONNECTED',
        details
      );
    case 'instance_check_failed':
      return t(
        'INBOX_MGMT.EVOLUTION_CONVERSION.ISSUES.INSTANCE_CHECK_FAILED',
        details
      );
    case 'instance_name_not_url_safe':
      return t(
        'INBOX_MGMT.EVOLUTION_CONVERSION.ISSUES.INSTANCE_NAME_NOT_URL_SAFE',
        details
      );
    case 'contacts_without_phone':
      return t(
        'INBOX_MGMT.EVOLUTION_CONVERSION.ISSUES.CONTACTS_WITHOUT_PHONE',
        details
      );
    case 'lid_only_contacts':
      return t(
        'INBOX_MGMT.EVOLUTION_CONVERSION.ISSUES.LID_ONLY_CONTACTS',
        details
      );
    case 'lid_disguised_as_phone':
      return t(
        'INBOX_MGMT.EVOLUTION_CONVERSION.ISSUES.LID_DISGUISED_AS_PHONE',
        details
      );
    case 'merge_conflicts':
      return t(
        'INBOX_MGMT.EVOLUTION_CONVERSION.ISSUES.MERGE_CONFLICTS',
        details
      );
    case 'orphaned_messages':
      return t(
        'INBOX_MGMT.EVOLUTION_CONVERSION.ISSUES.ORPHANED_MESSAGES',
        details
      );
    default:
      return issue.code;
  }
};
</script>

<template>
  <div class="flex flex-col gap-6 mx-6 max-w-4xl">
    <div>
      <span class="text-heading-3 text-n-slate-12">
        {{ t('INBOX_MGMT.EVOLUTION_CONVERSION.TITLE') }}
      </span>
      <p class="mt-1 text-body-main text-n-slate-11">
        {{ t('INBOX_MGMT.EVOLUTION_CONVERSION.DESCRIPTION') }}
      </p>
    </div>

    <div
      v-if="conversion"
      class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-1"
    >
      <div class="flex items-center gap-2">
        <span class="font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.EVOLUTION_CONVERSION.STATUS.TITLE') }}
        </span>
        <span
          class="px-2 py-0.5 text-xs rounded-full"
          :class="
            conversion.status === 'failed'
              ? 'bg-n-ruby-3 text-n-ruby-11'
              : 'bg-n-teal-3 text-n-teal-11'
          "
        >
          {{ statusLabel }}
        </span>
        <Spinner v-if="isRunning" :size="16" />
      </div>

      <p v-if="conversion.error_message" class="text-body-small text-n-ruby-11">
        {{ conversion.error_message }}
      </p>

      <div v-if="!isRunning" class="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div
          v-for="stat in resultStats"
          :key="stat.label"
          class="flex flex-col gap-1 p-3 border rounded-lg border-n-weak"
        >
          <span
            class="text-heading-3"
            :class="stat.warn ? 'text-n-amber-11' : 'text-n-slate-12'"
          >
            {{ stat.value ?? 0 }}
          </span>
          <span class="text-body-small text-n-slate-11">{{ stat.label }}</span>
        </div>
      </div>

      <div v-if="canRollback" class="flex flex-col gap-1 items-start">
        <NextButton
          ruby
          faded
          :label="t('INBOX_MGMT.EVOLUTION_CONVERSION.ROLLBACK.BUTTON')"
          @click="rollbackDialogRef?.open()"
        />
        <span class="text-body-small text-n-slate-11">
          {{ t('INBOX_MGMT.EVOLUTION_CONVERSION.ROLLBACK.HINT') }}
        </span>
      </div>
    </div>

    <template v-if="isApiInbox">
      <div v-if="isLoading" class="flex justify-center py-10">
        <Spinner />
      </div>

      <template v-else-if="report">
        <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-alpha-1">
          <div class="flex items-center gap-2">
            <span class="text-body-small text-n-slate-11">
              {{ t('INBOX_MGMT.EVOLUTION_CONVERSION.INSTANCE') }}
            </span>
            <span class="font-medium text-n-slate-12">
              {{ evolution.instance_name || '—' }}
            </span>
            <span
              v-if="evolution.checked"
              class="px-2 py-0.5 text-xs rounded-full"
              :class="
                evolution.connection_state === 'open'
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-amber-3 text-n-amber-11'
              "
            >
              {{ evolution.connection_state }}
            </span>
          </div>
          <span class="text-body-small text-n-slate-11">
            {{ evolution.server_url || '—' }}
          </span>
          <span
            v-if="evolution.phone_number"
            class="text-body-small text-n-slate-11"
          >
            {{ evolution.phone_number }}
          </span>
        </div>

        <Banner v-for="issue in blockers" :key="issue.code" color="ruby">
          {{ issueText(issue) }}
        </Banner>
        <Banner v-for="issue in warnings" :key="issue.code" color="amber">
          {{ issueText(issue) }}
        </Banner>

        <div
          v-for="section in sections"
          :key="section.title"
          class="flex flex-col gap-2"
        >
          <span class="font-medium text-n-slate-12">{{ section.title }}</span>
          <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div
              v-for="stat in section.stats"
              :key="stat.label"
              class="flex flex-col gap-1 p-3 border rounded-lg border-n-weak"
            >
              <span
                class="text-heading-3"
                :class="stat.warn ? 'text-n-amber-11' : 'text-n-slate-12'"
              >
                {{ stat.value ?? 0 }}
              </span>
              <span class="text-body-small text-n-slate-11">{{
                stat.label
              }}</span>
            </div>
          </div>
        </div>

        <div v-if="conflictSamples.length" class="flex flex-col gap-1">
          <span class="text-body-small text-n-slate-11">
            {{ t('INBOX_MGMT.EVOLUTION_CONVERSION.CONFLICTS.SAMPLES') }}
          </span>
          <span class="font-mono text-body-small text-n-slate-12">
            {{ conflictSamples.join(', ') }}
          </span>
        </div>
      </template>

      <div class="flex flex-col gap-2 pt-6 border-t border-n-weak">
        <span class="font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.EVOLUTION_CONVERSION.LIVE_CHECK.TITLE') }}
        </span>
        <p class="text-body-small text-n-slate-11">
          {{ t('INBOX_MGMT.EVOLUTION_CONVERSION.LIVE_CHECK.DESCRIPTION') }}
        </p>
        <div class="flex items-end gap-2">
          <NextInput
            v-model="apiKey"
            type="password"
            class="flex-1"
            :label="t('INBOX_MGMT.EVOLUTION_CONVERSION.LIVE_CHECK.API_KEY')"
            :placeholder="
              t(
                'INBOX_MGMT.EVOLUTION_CONVERSION.LIVE_CHECK.API_KEY_PLACEHOLDER'
              )
            "
          />
          <NextButton
            faded
            :label="t('INBOX_MGMT.EVOLUTION_CONVERSION.LIVE_CHECK.SUBMIT')"
            :is-loading="isLoading"
            @click="fetchReport"
          />
        </div>
      </div>

      <div class="flex flex-col gap-2 pt-6 border-t border-n-weak">
        <span class="font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.EVOLUTION_CONVERSION.RUN.TITLE') }}
        </span>
        <p class="text-body-small text-n-slate-11">
          {{ t('INBOX_MGMT.EVOLUTION_CONVERSION.RUN.DESCRIPTION') }}
        </p>
        <label class="flex gap-2 items-center text-body-small text-n-slate-11">
          <input v-model="consolidateConversations" type="checkbox" />
          {{ t('INBOX_MGMT.EVOLUTION_CONVERSION.RUN.CONSOLIDATE') }}
        </label>
        <div class="flex items-center gap-2">
          <NextButton
            :label="t('INBOX_MGMT.EVOLUTION_CONVERSION.RUN.BUTTON')"
            :disabled="!canConvert || !apiKey"
            @click="convertDialogRef?.open()"
          />
          <span v-if="!apiKey" class="text-body-small text-n-slate-11">
            {{ t('INBOX_MGMT.EVOLUTION_CONVERSION.RUN.API_KEY_REQUIRED') }}
          </span>
        </div>
      </div>
    </template>

    <p class="text-body-small text-n-slate-11">
      {{ t('INBOX_MGMT.EVOLUTION_CONVERSION.READ_ONLY_NOTE') }}
    </p>

    <Dialog
      ref="convertDialogRef"
      type="alert"
      :title="t('INBOX_MGMT.EVOLUTION_CONVERSION.RUN.CONFIRM_TITLE')"
      :description="
        t('INBOX_MGMT.EVOLUTION_CONVERSION.RUN.CONFIRM_DESCRIPTION')
      "
      :confirm-button-label="t('INBOX_MGMT.EVOLUTION_CONVERSION.RUN.BUTTON')"
      :is-loading="isSubmitting"
      @confirm="runConversion"
    />

    <Dialog
      ref="rollbackDialogRef"
      type="alert"
      :title="t('INBOX_MGMT.EVOLUTION_CONVERSION.ROLLBACK.CONFIRM_TITLE')"
      :description="
        t('INBOX_MGMT.EVOLUTION_CONVERSION.ROLLBACK.CONFIRM_DESCRIPTION')
      "
      :confirm-button-label="
        t('INBOX_MGMT.EVOLUTION_CONVERSION.ROLLBACK.BUTTON')
      "
      :is-loading="isSubmitting"
      @confirm="runRollback"
    />
  </div>
</template>
